import React, { useEffect, useState } from 'react'
import {
    CardContent,
    CardActionArea,
    Typography,
    Card,
    Grid,
    Button,
    Tooltip,
    Backdrop,
    CircularProgress,
    Snackbar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@material-ui/core'
import { useStyles } from './cardStyles'
import { Link, navigate } from '@reach/router'
import useOrder from '../../../hooks/useOrderContext'
import moment from 'moment'
import 'moment/locale/es'
import SmartNotes from '../smartNotes'
import { getCurrentUser } from '../../../firebase/obj/user'
import useMainContext from '../../../hooks/useMainContext'

moment.locale('es')

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const defaultSteps = [
    'Pendiente',
    'En preparación',
    'En reparto',
    'Entregado',
    'Cancelado'
]

export const OrderViewCard = ({ order, config, mode = 'card' }) => {
    const classes = useStyles()
    const { repeatOrder, loadOrders } = useOrder() // Import loadOrders
    const { user, businessInfo } = useMainContext()
    const [activeStep, setActiveStep] = useState(0)
    const [steps, setSteps] = useState(defaultSteps)
    const [showMercadoPagoBackdrop, setShowMercadoPagoBackdrop] = useState(false)
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' })
    const [openCancelModal, setOpenCancelModal] = useState(false);


    useEffect(() => {
        const { orderState, pickup } = order
        let {
            firstState = 'En cocina',
            delayState = 'Demorado',
            cookedState = 'Finalizado',
            assignedState = 'En reparto',
            lastState = 'Entregado',
            canceledState = 'Cancelado'
        } = config
        let step = 0
        if (orderState === firstState || orderState === delayState || (orderState === cookedState && !pickup))
            step = 1
        if (orderState === assignedState || (orderState === cookedState && pickup))
            step = 2
        if (orderState === lastState)
            step = 3
        if (orderState === canceledState)
            step = 4

        if (pickup) {
            setSteps([
                'Pendiente',
                'En preparación',
                'Para retirar',
                'Entregado',
            ])
        }

        setActiveStep(step)
    }, [order])

    const handleRepeatOrder = () => {
        repeatOrder(order)
        navigate("/carrito")
    }

    const fetchMercadoPagoPreferenceId = async (currentOrder) => {
        setShowMercadoPagoBackdrop(true);

        if (!user) {
            setNotification({
                open: true,
                autoHideDuration: 5000,
                severity: 'error',
                message: 'Debes iniciar sesión para pagar con Mercado Pago.',
            });
            throw new Error('User not logged in for Mercado Pago payment.');
        }

        try {
            const token = await getCurrentUser()?.getIdToken();
            if (!token) {
                throw new Error('Authentication token not found.');
            }

            const preferencePayload = {
                orderId: currentOrder.id,
                userId: user.uid,
                amount: parseFloat(Number(currentOrder?.amount || 0).toFixed(2)),
                currency_id: businessInfo?.currency?.code || "UYU",
                description: `Compra en ${process.env.REACT_APP_NAME || businessInfo?.companyName || 'tu tienda'}`,
            };

            const response = await fetch(`${API_BASE_URL}/mercadopago/preference/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(preferencePayload),
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Error creating Mercado Pago preference:', errorData);
                throw new Error(errorData.message || 'Error al crear la preferencia de pago.');
            }

            const data = await response.json();

            if (data.initPoint) {
                window.location.href = data.initPoint;
            } else {
                setShowMercadoPagoBackdrop(false);
                throw new Error('initPoint no recibido del backend.');
            }

        } catch (error) {
            setShowMercadoPagoBackdrop(false);
            console.error('fetchMercadoPagoPreferenceId error:', error);
            setNotification({
                open: true,
                autoHideDuration: 5000,
                severity: 'error',
                message: error.message || 'No se pudo generar la preferencia de pago.',
            });
            throw error;
        }
    };

    const handleOpenCancelModal = () => {
        setOpenCancelModal(true);
    };

    const handleCloseCancelModal = () => {
        setOpenCancelModal(false);
    };

    const handleCancelOrder = async () => {
        handleCloseCancelModal();
        try {
            const token = await getCurrentUser()?.getIdToken();
            if (!token) {
                throw new Error('Authentication token not found.');
            }

            const response = await fetch(`${API_BASE_URL}/orders/order/changeState`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    id: order.id,
                    state: 15,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al cancelar el pedido.');
            }

            setNotification({
                open: true,
                autoHideDuration: 5000,
                severity: 'success',
                message: 'Pedido cancelado correctamente.',
            });
            loadOrders(); // Refresh orders after successful cancellation

        } catch (error) {
            console.error('handleCancelOrder error:', error);
            setNotification({
                open: true,
                autoHideDuration: 5000,
                severity: 'error',
                message: error.message || 'No se pudo cancelar el pedido.',
            });
        }
    };

    const cardContent = () =>
    (
        <CardContent>
            <Grid
                container
                direction="row"
                justify="center"
                alignItems="center"
                className={classes.receiptContainer}
            >
                <Grid item xs={6}>
                    <Typography  >Cod: {order.cod}</Typography>
                </Grid>
                <Grid item xs={6} container justify="flex-end">
                    <Typography> {moment(new Date(order.date.seconds * 1000)).format('DD/MM/YYYY HH:mm')}</Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography  >Estado: {steps[activeStep] || order.orderState || ''}</Typography>
                </Grid>
                <Grid item xs={12}>
                    {
                        order?.cancelledMessage && <Typography>Motivo: {order.cancelledMessage}</Typography>
                    }
                </Grid>
                <Grid item xs={12} container style={{ padding: 10, maxHeight: 100, overflow: 'auto' }}>
                    {
                        order?.productsList?.map((product, index) => (
                            <Grid container key={index} >
                                <Grid item xs={1}>
                                    <Typography>{product.qty}</Typography>
                                </Grid>
                                <Grid item xs={11}>
                                    <Typography >{product.name} </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <SmartNotes smartNotes={product.smartNotes} typographyColor='textSecondary' typographySize={13} />
                                </Grid>
                            </Grid>
                        ))
                    }
                </Grid>
                <Grid item xs={12} style={{ paddingTop: 10 }}>
                    <Typography  >Total: ${order.amount || 0}</Typography>
                </Grid>

                {
                    order.orderState === 'Pago pendiente' &&
                    <Grid container spacing={1} style={{ paddingTop: 10 }}>
                        <Grid item xs={6}>
                            <Button
                                variant='contained'
                                color='primary'
                                size='small'
                                fullWidth
                                onClick={() => fetchMercadoPagoPreferenceId(order)}
                            >Pagar</Button>
                        </Grid>
                        <Grid item xs={6}>
                            <Button
                                variant='outlined'
                                color='secondary'
                                size='small'
                                fullWidth
                                onClick={handleOpenCancelModal}
                            >Cancelar</Button>
                        </Grid>
                    </Grid>
                }

                {
                    mode === 'history' &&
                    <Grid item xs={12} style={{ paddingTop: 10 }}>
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            fullWidth
                            disabled={false}
                            onClick={handleRepeatOrder}
                        >Repetir pedido</Button>
                    </Grid>
                }

            </Grid>
        </CardContent>

    )



    return (
        <>
            <Backdrop open={showMercadoPagoBackdrop} style={{ zIndex: 9999, color: '#fff' }}>
                <CircularProgress color="inherit" />
                <h4 style={{ marginLeft: 15 }}>Redirigiendo a Mercado Pago...</h4>
            </Backdrop>
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={notification.open}
                autoHideDuration={notification.autoHideDuration}
                onClose={() => setNotification({ ...notification, open: false })}
                message={notification.message}
                severity={notification.severity}
            />
            <Dialog
                open={openCancelModal}
                onClose={handleCloseCancelModal}
            >
                <DialogTitle>Cancelar Pedido</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Estás seguro de que quieres cancelar este pedido?
                        {order.usedPoints > 0 && ` Se te devolverán ${order.usedPoints} puntos.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCancelModal} color="primary">
                        No
                    </Button>
                    <Button onClick={handleCancelOrder} color="primary" autoFocus>
                        Sí, cancelar
                    </Button>
                </DialogActions>
            </Dialog>
            <Card className={classes.root}>
                {
                    mode === 'history' ?
                        cardContent() :
                        <CardActionArea component={Link} to={`/order/${order.id}`}>
                            {cardContent()}
                        </CardActionArea>
                }
            </Card>
        </>
    )
}

export default OrderViewCard
